import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import { DateTime } from 'luxon';
import React, { useEffect } from 'react';
import { WO2Select } from 'src/common/components/Select';
import { CashAccountSecurity } from '../store/types';

export const defaultCurrencyCode = 'AUD';

interface Props {
  securities: CashAccountSecurity[];
  selectedSecurityId: number | null;
  setSecurityParameter: (securityId: number) => void;
}

const tradeDateMillis = (security: CashAccountSecurity): number => {
  const tradeDate = DateTime.fromISO(security.mostRecentTradeDate ?? '');
  return tradeDate.isValid ? tradeDate.toMillis() : 0;
};

export const formatSecurityLabel = (security: CashAccountSecurity): string =>
  !security.securityName ? security.securityCode : `${security.securityCode} (${security.securityName})`;

export const selectDefaultSecurity = (securities: CashAccountSecurity[]): CashAccountSecurity | null => {
  if (securities.length === 0) {
    return null;
  }

  return (
    securities.find((security) => security.currencyCode === defaultCurrencyCode) ??
    [...securities].sort((a, b) => tradeDateMillis(b) - tradeDateMillis(a))[0]
  );
};

export const SecuritySelector = ({ securities, selectedSecurityId, setSecurityParameter }: Props): JSX.Element => {
  const selectedSecurity = securities.find((security) => security.securityId === selectedSecurityId);

  useEffect(() => {
    if (securities.some((security) => security.securityId === selectedSecurityId)) {
      return;
    }

    const defaultSecurity = selectDefaultSecurity(securities);
    if (defaultSecurity) {
      setSecurityParameter(defaultSecurity.securityId);
    }
  }, [securities, selectedSecurityId, setSecurityParameter]);

  return (
    <FormControl variant="outlined" fullWidth>
      <InputLabel id="security-select-label">Security</InputLabel>
      <WO2Select
        labelId="security-select-label"
        id="security-select"
        label="Security"
        value={selectedSecurity?.securityId ?? ''}
        onChange={(event) => setSecurityParameter(Number(event.target.value))}
        SelectDisplayProps={
          // Always add a title attribute, providing a native browser tooltip on hover. This lets users read the full label if it's truncated.
          { title: selectedSecurity ? formatSecurityLabel(selectedSecurity) : '' }
        }
      >
        {securities.map((security) => (
          <MenuItem key={security.securityId} value={security.securityId}>
            {formatSecurityLabel(security)}
          </MenuItem>
        ))}
      </WO2Select>
    </FormControl>
  );
};
